import { HexColor } from '@uiw/react-color';

/**
 * Converts HEX color to HSL values
 */
export const hexToHsl = (hex: string): { h: number; s: number; l: number } => {
  // Remove # if present
  hex = hex.replace(/^#/, '');

  // Parse hex values
  const r = parseInt(hex.substring(0, 2), 16) / 255;
  const g = parseInt(hex.substring(2, 4), 16) / 255;
  const b = parseInt(hex.substring(4, 6), 16) / 255;

  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  let h = 0;
  let s = 0;
  const l = (max + min) / 2;

  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);

    switch (max) {
      case r:
        h = ((g - b) / d + (g < b ? 6 : 0)) / 6;
        break;
      case g:
        h = ((b - r) / d + 2) / 6;
        break;
      case b:
        h = ((r - g) / d + 4) / 6;
        break;
    }
  }

  return {
    h: Math.round(h * 360),
    s: Math.round(s * 100),
    l: Math.round(l * 100),
  };
};

/**
 * Converts HSL values to HEX color
 */
export const hslToHex = (h: number, s: number, l: number): string => {
  s /= 100;
  l /= 100;

  const c = (1 - Math.abs(2 * l - 1)) * s;
  const x = c * (1 - Math.abs(((h / 60) % 2) - 1));
  const m = l - c / 2;

  let r = 0;
  let g = 0;
  let b = 0;

  if (h >= 0 && h < 60) {
    r = c;
    g = x;
    b = 0;
  } else if (h >= 60 && h < 120) {
    r = x;
    g = c;
    b = 0;
  } else if (h >= 120 && h < 180) {
    r = 0;
    g = c;
    b = x;
  } else if (h >= 180 && h < 240) {
    r = 0;
    g = x;
    b = c;
  } else if (h >= 240 && h < 300) {
    r = x;
    g = 0;
    b = c;
  } else if (h >= 300 && h < 360) {
    r = c;
    g = 0;
    b = x;
  }

  const toHex = (n: number) => {
    const hex = Math.round((n + m) * 255).toString(16);
    return hex.length === 1 ? '0' + hex : hex;
  };

  return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
};

/**
 * Validates if a string is a valid HEX color (6 or 8 digits, optional 3/4 shorthand)
 */
export const isValidHex = (hex: string): boolean => {
  return /^#?([0-9A-Fa-f]{6}|[0-9A-Fa-f]{3}|[0-9A-Fa-f]{8}|[0-9A-Fa-f]{4})$/.test(hex);
};

/**
 * Normalizes HEX color to 6- or 8-character format with #
 */
export const normalizeHex = (hex: string): HexColor => {
  hex = hex.replace(/^#/, '');

  // Convert 3-char to 6-char, 4-char to 8-char
  if (hex.length === 3) {
    hex = hex
      .split('')
      .map((c) => c + c)
      .join('');
  } else if (hex.length === 4) {
    hex = hex
      .split('')
      .map((c) => c + c)
      .join('');
  }

  return `#${hex.toUpperCase()}`;
};

/** Returns 6-digit hex (no alpha). For 8-digit hex strips the alpha channel. */
export const getHexWithoutAlpha = (hex: string): string => {
  const normalized = normalizeHex(hex);
  if (normalized.length === 9) return normalized.slice(0, 7);
  return normalized;
};

/** Returns alpha 0–1 from hex. For 6-digit hex returns 1. */
export const getHexAlpha = (hex: string): number => {
  const h = hex.replace(/^#/, '');
  if (h.length === 8) {
    return parseInt(h.slice(6, 8), 16) / 255;
  }
  return 1;
};

/** True if color is fully transparent: literal "transparent" or 8-digit hex with alpha 0. */
export const isFullyTransparent = (value: string): boolean => {
  if (value?.toLowerCase() === 'transparent') return true;
  if (/^#?[0-9A-Fa-f]{8}$/.test(value?.replace(/^#/, ''))) return getHexAlpha(value) === 0;
  return false;
};
